import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Assignment as SubmissionIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Task as TaskIcon,
  CheckCircle as CompletedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import api from '../../../api';
import { showSuccess, showError, showInfo } from '../../../utils/sweetAlertConfig';

const PRIMARY = '#29204C';
const SUCCESS = '#68B977';

export default function TechnicalSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [reviewData, setReviewData] = useState({
    score: '',
    feedback: '',
    status: 'under_review'
  });
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/technical-submissions');
      console.log('Submissions loaded:', response.data);
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Error loading submissions:', error);
      showError('Error', 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (submission) => {
    setSelectedSubmission(submission);
    setShowViewModal(true);
  };

  const handleReview = (submission) => {
    setSelectedSubmission(submission);
    setReviewData({
      score: submission.score || '',
      feedback: submission.feedback || '',
      status: submission.status || 'under_review'
    });
    setShowReviewModal(true);
  };

  const handleDownload = async (submission) => {
    if (!submission.file_path) {
      showInfo('No File', 'This submission does not have a file to download.');
      return;
    }

    try {
      const response = await api.get(`/technical-submissions/${submission.submission_id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', submission.file_name || 'submission_file');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('Downloaded', 'File downloaded successfully');
    } catch (error) {
      console.error('Error downloading file:', error);
      showError('Error', 'Failed to download file');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/technical-submissions/${selectedSubmission.submission_id}/review`, reviewData);
      showSuccess('Success', 'Review submitted successfully');
      setShowReviewModal(false);
      loadSubmissions();
    } catch (error) {
      console.error('Error submitting review:', error);
      showError('Error', 'Failed to submit review');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      submitted: { label: 'Submitted', color: '#1976d2', icon: <PendingIcon fontSize="small" /> },
      under_review: { label: 'Under Review', color: '#ed6c02', icon: <PendingIcon fontSize="small" /> },
      completed: { label: 'Completed', color: SUCCESS, icon: <CompletedIcon fontSize="small" /> },
      rejected: { label: 'Rejected', color: '#d32f2f', icon: <RejectedIcon fontSize="small" /> }
    };

    const config = statusConfig[status] || { label: status, color: '#757575', icon: <PendingIcon fontSize="small" /> };

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          bgcolor: `${config.color}15`,
          color: config.color,
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: config.color,
          },
        }}
      />
    );
  };

  const getScoreBadge = (score, maxScore) => {
    if (!score) return <Typography variant="caption" color="text.secondary">Not scored</Typography>;

    const percentage = (score / maxScore) * 100;
    let color = SUCCESS;

    if (percentage < 50) color = '#d32f2f';
    else if (percentage < 75) color = '#ed6c02';

    return (
      <Typography variant="body2" sx={{ fontWeight: 700, color }}>
        {score}/{maxScore} ({percentage.toFixed(0)}%)
      </Typography>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSubmissions = submissions.filter(submission => {
    const matchesStatus = filterStatus === 'all' || submission.status === filterStatus;
    const matchesSearch =
      submission.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.task_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.job_title?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const paginatedSubmissions = filteredSubmissions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns = [
    {
      field: 'candidate_name',
      label: 'Candidate & Task',
      minWidth: 250,
      render: (row) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Avatar sx={{ bgcolor: PRIMARY, width: 32, height: 32 }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY }}>
              {row.candidate_name || 'Unknown Candidate'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
            Task: {row.task_title || 'N/A'}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
            Job: {row.job_title || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'submitted_at',
      label: 'Submission Details',
      minWidth: 200,
      render: (row) => (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(row.submitted_at)}
          </Typography>
          {row.file_name && (
            <Typography variant="caption" sx={{ display: 'block', color: '#1976d2', mt: 0.5 }}>
              {row.file_name}
            </Typography>
          )}
          {row.file_size_kb && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Size: {(row.file_size_kb / 1024).toFixed(1)} MB
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      label: 'Status & Score',
      width: 180,
      align: 'center',
      render: (row) => (
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ mb: 1 }}>
            {getStatusChip(row.status)}
          </Box>
          <Box sx={{ mb: 0.5 }}>
            {getScoreBadge(row.score, row.max_score)}
          </Box>
          {row.reviewed_by && (
            <Typography variant="caption" color="text.secondary">
              Reviewed by User #{row.reviewed_by}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Technical Submissions"
        subtitle="Review and evaluate candidate technical submissions"
        icon={SubmissionIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Technical Submissions' },
        ]}
      />

      {/* Search and Filter */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder="Search by candidate name, task, or job position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: PRIMARY,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: PRIMARY,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white',
              },
            }}
          >
            <MenuItem value="all">All Submissions</MenuItem>
            <MenuItem value="submitted">New Submissions</MenuItem>
            <MenuItem value="under_review">Under Review</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {/* Submissions Table */}
      <ModernTable
        columns={columns}
        data={paginatedSubmissions}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={filteredSubmissions.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onView={handleView}
        onEdit={handleReview}
        emptyMessage="No technical submissions have been received yet"
        showActions={true}
        onDelete={null}
      />

      {/* View Details Dialog */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: PRIMARY,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2.5,
          }}
        >
          <ViewIcon />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
            Submission Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedSubmission && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Candidate Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedSubmission.candidate_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Task Title
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedSubmission.task_title || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Job Position
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedSubmission.job_title || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Submitted At
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {formatDateTime(selectedSubmission.submitted_at)}
                </Typography>
              </Grid>
              {selectedSubmission.file_name && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    File Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {selectedSubmission.file_name}
                  </Typography>
                </Grid>
              )}
              {selectedSubmission.file_size_kb && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    File Size
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {(selectedSubmission.file_size_kb / 1024).toFixed(1)} MB
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {getStatusChip(selectedSubmission.status)}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Score
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {getScoreBadge(selectedSubmission.score, selectedSubmission.max_score)}
                </Box>
              </Grid>
              {selectedSubmission.submission_text && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Submission Text
                  </Typography>
                  <Card sx={{ bgcolor: '#f8f9fa', mb: 2 }}>
                    <CardContent>
                      <Typography variant="body2">
                        {selectedSubmission.submission_text}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {selectedSubmission.submission_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Candidate Notes
                  </Typography>
                  <Card sx={{ bgcolor: '#f8f9fa', mb: 2 }}>
                    <CardContent>
                      <Typography variant="body2">
                        {selectedSubmission.submission_notes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {selectedSubmission.feedback && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reviewer Feedback
                  </Typography>
                  <Card sx={{ bgcolor: '#e3f2fd', mb: 2 }}>
                    <CardContent>
                      <Typography variant="body2">
                        {selectedSubmission.feedback}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          {selectedSubmission?.file_path && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(selectedSubmission)}
              sx={{
                borderRadius: 2,
                px: 3,
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: '#e3f2fd',
                },
              }}
            >
              Download File
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => setShowViewModal(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: '#666',
              color: '#666',
              '&:hover': {
                borderColor: '#333',
              },
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setShowViewModal(false);
              handleReview(selectedSubmission);
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              bgcolor: PRIMARY,
              '&:hover': {
                bgcolor: '#1f1840',
              },
            }}
          >
            Review This Submission
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Modal */}
      <Dialog
        open={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: PRIMARY,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2.5,
          }}
        >
          <EditIcon />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
            Review Submission
          </Typography>
        </DialogTitle>

        <form onSubmit={handleReviewSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            {selectedSubmission && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card sx={{ bgcolor: '#f8f9fa' }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Candidate:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedSubmission.candidate_name || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Task:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedSubmission.task_title || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Score *"
                    value={reviewData.score}
                    onChange={(e) => setReviewData({ ...reviewData, score: e.target.value })}
                    inputProps={{
                      min: 0,
                      max: selectedSubmission.max_score || 100,
                      step: 0.01
                    }}
                    required
                    placeholder={`Max: ${selectedSubmission.max_score || 100}`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: PRIMARY,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: PRIMARY,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: PRIMARY,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Status *"
                    value={reviewData.status}
                    onChange={(e) => setReviewData({ ...reviewData, status: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: PRIMARY,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: PRIMARY,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: PRIMARY,
                      },
                    }}
                  >
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', pt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Current Score
                    </Typography>
                    <Box>
                      {getScoreBadge(selectedSubmission.score, selectedSubmission.max_score)}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Feedback"
                    value={reviewData.feedback}
                    onChange={(e) => setReviewData({ ...reviewData, feedback: e.target.value })}
                    placeholder="Provide detailed feedback for the candidate..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: PRIMARY,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: PRIMARY,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: PRIMARY,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setShowReviewModal(false)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                borderColor: '#666',
                color: '#666',
                '&:hover': {
                  borderColor: '#333',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<CompletedIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                bgcolor: SUCCESS,
                '&:hover': {
                  bgcolor: '#5a9e67',
                },
              }}
            >
              Submit Review
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}
